import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, throwError, EMPTY } from 'rxjs';
import { AuthService } from '../Service/auth.service';

/**
 * HTTP Interceptor for handling authentication errors globally
 * 
 * Intercepts all HTTP responses and:
 * - 401 (Unauthorized): Token expired/invalid → Clears auth state and redirects to login
 *   Exception: During login flow, errors propagate to component for user feedback
 * - 403 (Forbidden): Insufficient permissions → Redirects to access-denied (auth preserved)
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status === 401) {
        console.error('Authentication failed (401): Token expired or invalid');
        
        // Let login component handle login errors
        if (router.url.startsWith('/login')) {
          return throwError(() => error);
        }
        
        authService.handleAuthenticationFailure();
        
        const returnUrl = router.url;
        router.navigate(['/login'], { 
          queryParams: { 
            sessionExpired: 'true',
            returnUrl: returnUrl
          },
          replaceUrl: true
        });
        
        return EMPTY;
      }

      if (error.status === 403) {
        console.error('Access forbidden (403): Insufficient permissions', error);

        const returnUrl = router.url;
        router.navigate(['/access-denied'], { 
          queryParams: { returnUrl: returnUrl },
          replaceUrl: true 
        });
        
        return EMPTY;
      }

      return throwError(() => error);
    })
  );
};
